import { Dropdown, MenuProps, Tooltip, Modal } from 'antd';
import cls from 'classnames';
import { useTranslation } from 'next-i18next';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import styles from './index.module.scss';
import arrowDownIcon from '../../../assets/Edit/arrowDown.png';
import closeIcon from '../../../assets/Edit/close.png';
import infoIcon from '../../../assets/Edit/info.svg';
import selectIcon from '../../../assets/Edit/select.svg';
import unselectIcon from '../../../assets/Edit/unselect.svg';
import { useLLModels, useOnceValue } from '../../../hooks';
import { useAppSelector, useAppDispatch, updateSettingInfo } from '../../../state';
import { LOCAL_STORAGE_KEYS } from '../../../storage';
import { track } from '../../../track';
import { ChatModelType } from '../../../types';
import { getDialogModelChoice, PAGE } from '../../EditHeader';
import { ENpcStatusTrackText } from '../../Home';
import { getNpcStatus } from '../../Home/NpcList';
import { LocalImage } from '../../Image';

function ConfirmComponent({
  onClose,
  onOK,
  noMoreShow,
}: {
  onClose: () => void;
  onOK: () => void;
  noMoreShow: () => void;
}) {
  const { t } = useTranslation();
  const [select, setSelect] = useState(false);

  return (
    <div className={styles.modalConfirm}>
      <div className={styles.title}>
        {t('selectModelTitle')}
        <LocalImage src={closeIcon} onClick={onClose} />
      </div>
      <p className={styles.desc}>{t('selectModelDescription')}</p>
      <div className={styles.bottom}>
        <LocalImage src={select ? selectIcon : unselectIcon} onClick={() => setSelect((s) => !s)} />
        <p onClick={() => setSelect((s) => !s)}>{t('noMoreTip')}</p>
        <div className={styles.btn} onClick={onClose}>
          {t('cancel')}
        </div>
        <div
          className={cls(styles.btn, styles.ok)}
          onClick={() => {
            if (select) noMoreShow();
            onOK();
            onClose();
          }}
        >
          {t('confirm')}
        </div>
      </div>
    </div>
  );
}

export function ModalSelect() {
  const { models } = useLLModels();
  const { chat_model_type } = useAppSelector((state) => state.edit.settingInfo);
  const { npcId, statusInfo } = useAppSelector((state) => state.edit);
  const status = getNpcStatus(statusInfo);

  const selectedModel = useMemo(
    () => models.find((model) => model.id === chat_model_type),
    [chat_model_type, models]
  );

  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { enable, trigger, close } = useOnceValue(LOCAL_STORAGE_KEYS.SwitchModelConfirm, false);
  const selectingModel = useRef<ChatModelType>(chat_model_type || ChatModelType.Chat);

  const onClose = () => {
    track.send('dialog_model_choice_popup_click', PAGE, {
      npc_id: npcId,
      ugc_npc_approving_status: ENpcStatusTrackText[status],
      dialog_model_choice: chat_model_type,
      is_show_agiain: enable ? 'true' : 'false',
      model_choice_popup_click_type: 'cancel',
    });

    setShow(false);
  };

  const onOK = useCallback(() => {
    track.send('dialog_model_choice_popup_click', PAGE, {
      npc_id: npcId,
      ugc_npc_approving_status: ENpcStatusTrackText[status],
      dialog_model_choice: selectingModel.current,
      is_show_agiain: enable ? 'true' : 'false',
      model_choice_popup_click_type: 'confirm',
    });

    dispatch(
      updateSettingInfo({
        chat_model_type: selectingModel.current,
      })
    );
  }, [dispatch]);

  const onSelectModel = useCallback(
    (modelType: ChatModelType) => {
      if (chat_model_type === modelType) return;

      track.send('dialog_model_choice_click', PAGE, {
        npc_id: npcId,
        ugc_npc_approving_status: ENpcStatusTrackText[status],
        dialog_model_choice: modelType,
      });

      selectingModel.current = modelType;
      if (enable) {
        setShow(true);
      } else {
        onOK();
      }
    },
    [chat_model_type, enable, onOK]
  );

  const items: MenuProps['items'] = useMemo(
    () => [
      {
        key: '1',
        type: 'group',
        label: t('modelSelectTitle'),
        children: models.map((item) => ({
          key: item.id,
          onClick: () => onSelectModel(item.id),
          label: (
            <div className={styles.item}>
              <img className={styles.logo} src={item.icon} />
              <p>{item.name}</p>
              <Tooltip
                title={
                  <div className={styles.modelDesc}>
                    <h2>{t('modelDescTitle')}:</h2>
                    <p>{item.desc}</p>
                  </div>
                }
                color="#282A2E"
                placement="bottom"
              >
                <LocalImage className={styles.icon} src={infoIcon} />
              </Tooltip>
            </div>
          ),
        })),
      },
    ],
    [models, onSelectModel, t]
  );

  const [show, setShow] = useState(false);

  useEffect(() => {
    if (show) {
      track.send('dialog_model_choice_popup_view', PAGE, {
        npc_id: npcId,
        ugc_npc_approving_status: ENpcStatusTrackText[status],
        dialog_model_choice: getDialogModelChoice(chat_model_type),
      });
    }
  }, [chat_model_type, npcId, show, status]);

  return (
    <>
      <Dropdown menu={{ items }} overlayClassName={styles.dropdown}>
        <div className={cls(styles.selectBtn)}>
          <img className={styles.logo} src={selectedModel?.icon} />
          <p>{selectedModel?.name}</p>
          <LocalImage className={styles.arrow} src={arrowDownIcon} />
        </div>
      </Dropdown>
      {show && (
        <Modal
          open={show}
          footer={null}
          closeIcon={null}
          centered
          styles={{ content: { borderRadius: 12 } }}
        >
          <ConfirmComponent
            onOK={onOK}
            onClose={onClose}
            noMoreShow={() => {
              trigger();
              close();
            }}
          />
        </Modal>
      )}
    </>
  );
}
